import { useState } from "react";
import axios from "axios";

import { API_BASE } from "../../lib/api";
import { SOURCE_LABEL } from "../../lib/renterDrivers";
import DriverDetailsForm, { emptyDetails, toPayload } from "./DriverDetailsForm";

// Task 6: "Who will drive?" on the public booking form. Requirements are shown
// before the customer books, with their source. Licence details are optional
// here (unless the business requires them) and an early check says "likely
// eligible — still needs review", never "approved".
export default function WhoWillDrive({ companyId, vehicle, start, end, renterName, value, onChange }) {
  // What the customer typed lives in the parent's value, not in this
  // component: switching to "Hire a driver" and back unmounts this form, and
  // details already entered must survive that without being silently kept
  // in the payload while the fields look empty.
  const [screen, setScreen] = useState(null);
  const withDetails = !!value.detailsOpen;
  const details = value.form || emptyDetails(renterName || "");
  const requirements = vehicle?.self_drive_requirements || [];

  const update = (patch) => onChange({ ...value, ...patch });
  const check = async (next) => {
    const payload = toPayload({ ...next, legal_name: next.legal_name || renterName || "Driver" });
    update({ form: next, details: payload });
    if (!start || !end || !payload.licence.expiry_date) return;
    try {
      const { data } = await axios.post(`${API_BASE}/public/business/${companyId}/driver-screening`,
        { item_id: vehicle.id, start_datetime: start, end_datetime: end, details: payload });
      setScreen(data);
    } catch { setScreen(null); }
  };

  return (
    <fieldset className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="public-who-will-drive">
      <legend className="px-1 text-sm font-semibold">Who will drive?</legend>
      <label className="flex items-center gap-2 text-sm"><input type="radio" name="who-drives" checked={value.renterDriving}
             onChange={() => update({ renterDriving: true })} data-testid="public-i-will-drive" /> I will drive</label>
      <label className="flex items-center gap-2 text-sm"><input type="radio" name="who-drives" checked={!value.renterDriving}
             onChange={() => update({ renterDriving: false })} /> Someone else will be the primary driver</label>
      {requirements.length > 0 && (
        <ul className="text-xs text-slate-700 space-y-0.5" data-testid="public-driver-requirements">
          {requirements.map((r) => <li key={r.key}>{r.text} <span className="text-slate-500">— {SOURCE_LABEL[r.source]}</span></li>)}
        </ul>
      )}
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={withDetails}
             onChange={(e) => update(e.target.checked
               ? { detailsOpen: true, ...(value.form ? { details: toPayload(value.form) } : {}) }
               : { detailsOpen: false, details: null })} />
        Add the driver&apos;s licence details now (you can also do this later)</label>
      {withDetails && (
        <>
          <DriverDetailsForm value={details} idPrefix="public-driver"
                             onChange={(next) => check(next)} />
          {screen && (
            <p className={`text-xs rounded p-2 border ${screen.result === "NOT_ELIGIBLE" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}
               data-testid="public-driver-screening">
              {screen.label}{screen.messages?.length ? `: ${screen.messages.join(" ")}` : ""}
            </p>
          )}
        </>
      )}
      <p className="text-[11px] text-slate-500">Only the drivers listed on the booking may drive. Licences are checked for this vehicle and these dates before pickup.</p>
    </fieldset>
  );
}
